/**
 * Minimal crash breadcrumb for remote iOS debugging.
 *
 * On a GPU/memory crash, iOS WKWebView reloads the page — wiping the console,
 * which we can't read remotely anyway. So we persist a "current phase"
 * breadcrumb + any captured error to localStorage, and surface the last one
 * ON SCREEN on the next boot (so a remote player can just screenshot it).
 *
 * Everything is try/catch-guarded and best-effort — this must never itself
 * throw or affect gameplay.
 */

const PHASE_KEY = 'sdl_phase';
const CRASH_KEY = 'sdl_crash';

/** Record the current high-level phase. Cheap; call at risky transitions. */
export function markPhase(phase: string): void {
  try {
    localStorage.setItem(PHASE_KEY, JSON.stringify({ phase, t: Date.now() }));
  } catch { /* ignore */ }
}

/** Clear the phase once a risky transition completes safely. If the page is
 *  killed (iOS OOM) before this runs, the phase persists and is surfaced on the
 *  next boot — no time window needed. */
export function clearPhase(): void {
  try {
    localStorage.removeItem(PHASE_KEY);
  } catch { /* ignore */ }
}

function record(type: string, message: string): void {
  let phase = '';
  try {
    const raw = localStorage.getItem(PHASE_KEY);
    if (raw) phase = JSON.parse(raw).phase ?? '';
  } catch { /* ignore */ }
  try {
    localStorage.setItem(CRASH_KEY, JSON.stringify({
      type,
      message: String(message).slice(0, 400),
      phase,
      t: Date.now(),
    }));
  } catch { /* ignore */ }
}

export function installCrashReport(): void {
  window.addEventListener('error', (e) => record('error', e.message || String((e as ErrorEvent).error ?? 'error')));
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const r = e.reason as { message?: string } | undefined;
    record('promise', r && r.message ? r.message : String(r));
  });

  // webglcontextlost is THE signal for a GPU-process crash. Attach once the
  // canvas exists (it's created a bit after boot).
  const attachGl = (tries = 0) => {
    const c = document.querySelector('canvas');
    if (c) {
      c.addEventListener('webglcontextlost', () => record('webglcontextlost', 'GPU context lost'));
    } else if (tries < 20) {
      setTimeout(() => attachGl(tries + 1), 1000);
    }
  };
  attachGl();

  consumePrior();
}

// The on-screen red crash banner was a remote-debugging aid and is NOT shown in
// production. We still consume any stored breadcrumb on boot so stale crash /
// phase data doesn't accumulate in localStorage across sessions. The recording
// path (markPhase / record) stays in place for future diagnostics.
function consumePrior(): void {
  try {
    localStorage.removeItem(CRASH_KEY);
    localStorage.removeItem(PHASE_KEY);
  } catch { /* ignore */ }
}
